import { memo, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { ConfirmationModal } from "../../../shared/components/ConfirmationModal";
import { DebouncedInput } from "../../../shared/components/inputs";
import { TestLine, TestPassage } from "../types/test-detail.types";
import { DEBOUNCE_DELAY } from "../utils/constants";
import { LineEditorModal } from "./LineEditorModal";
import { PassageImageManager } from "./PassageImageManager";

interface PassageEditorProps {
  passage: TestPassage;
  passageIndex: number;
  onUpdate: (passage: TestPassage) => void;
  onRemove: () => void;
  isExpanded?: boolean;
  isIndividuallyExpanded?: boolean;
  onToggleExpansion?: (passageId: string, isExpanded: boolean) => void;
  testId?: string; // Test ID for image storage
}

function PassageEditor({
  passage,
  passageIndex,
  onUpdate,
  onRemove,
  isExpanded: controlledExpanded,
  isIndividuallyExpanded,
  onToggleExpansion,
  testId,
}: PassageEditorProps) {
  // Initialize local state with the controlled value if provided
  const [localExpanded, setLocalExpanded] = useState(() =>
    controlledExpanded !== undefined ? controlledExpanded : true
  );

  // Update local state when controlled state changes
  useEffect(() => {
    if (controlledExpanded !== undefined) {
      setLocalExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  // If individual expansion state is provided, use it instead
  const isExpanded =
    isIndividuallyExpanded !== undefined
      ? isIndividuallyExpanded
      : localExpanded;

  // Function to handle expansion toggle
  const handleToggleExpansion = () => {
    // Update local state
    const newExpandedState = !isExpanded;
    setLocalExpanded(newExpandedState);

    // If onToggleExpansion callback is provided, call it
    if (onToggleExpansion) {
      onToggleExpansion(passage.id, newExpandedState);
    }
  };
  const [showLineEditorModal, setShowLineEditorModal] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState<number | null>(null);
  const [newLineText, setNewLineText] = useState("");
  const [startLineNumber, setStartLineNumber] = useState(
    passage.lines.length > 0 ? passage.lines[0].number : 1
  );

  // State for confirmation modal
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [lineToDelete, setLineToDelete] = useState<number | null>(null);

  // Update passage title with debounce
  const updateTitle = useDebouncedCallback((title: string) => {
    onUpdate({
      ...passage,
      title,
    });
  }, DEBOUNCE_DELAY);

  // Update a specific line with debounce
  const updateLine = useDebouncedCallback((lineIndex: number, text: string) => {
    const updatedLines = [...passage.lines];
    updatedLines[lineIndex] = {
      ...updatedLines[lineIndex],
      text,
    };

    onUpdate({
      ...passage,
      lines: updatedLines,
    });
  }, DEBOUNCE_DELAY);

  // Add a new line
  const addLine = () => {
    if (!newLineText.trim()) return;

    const newLineNumber =
      passage.lines.length > 0
        ? Math.max(...passage.lines.map((line) => line.number)) + 1
        : 1;

    const newLine: TestLine = {
      number: newLineNumber,
      text: newLineText,
    };

    onUpdate({
      ...passage,
      lines: [...passage.lines, newLine],
    });

    setNewLineText("");
  };

  // Show confirmation modal before removing a line
  const promptRemoveLine = (lineIndex: number) => {
    setLineToDelete(lineIndex);
    setShowDeleteConfirmation(true);
  };

  // Remove a line and renumber remaining lines
  const removeLine = () => {
    if (lineToDelete === null) return;

    const updatedLines = [...passage.lines];
    updatedLines.splice(lineToDelete, 1);

    // Renumber the lines sequentially
    const renumberedLines = updatedLines.map((line, index) => ({
      ...line,
      number: startLineNumber + index,
    }));

    onUpdate({
      ...passage,
      lines: renumberedLines,
    });

    // Reset state
    setLineToDelete(null);
    setShowDeleteConfirmation(false);
  };

  // Update the starting line number and recalculate all line numbers with debounce
  const updateStartLineNumber = useDebouncedCallback(
    (newStartNumber: number) => {
      const startNum = Math.max(1, newStartNumber);
      setStartLineNumber(startNum);

      // Recalculate all line numbers
      const updatedLines = passage.lines.map((line, index) => ({
        ...line,
        number: startNum + index,
      }));

      onUpdate({
        ...passage,
        lines: updatedLines,
      });
    },
    DEBOUNCE_DELAY
  );

  return (
    <>
      <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden w-full">
        {/* Passage Header */}
        <div className="bg-gray-50 dark:bg-gray-700 p-3 flex justify-between items-center">
          <div className="flex items-center w-[calc(100%-150px)]">
            <button
              onClick={handleToggleExpansion}
              className="mr-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg
                className={`w-4 h-4 transform ${isExpanded ? "rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
            <DebouncedInput
              type="text"
              value={passage.title || ""}
              onChange={(value) => updateTitle(value as string)}
              className="font-medium bg-transparent border-b border-dashed border-gray-300 dark:border-gray-600 focus:outline-none focus:border-indigo-500 text-gray-800 dark:text-gray-200 w-full"
              placeholder="Passage title"
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowLineEditorModal(true)}
              className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md"
            >
              Edit Lines
            </button>
            <button
              onClick={onRemove}
              className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-md"
            >
              Remove
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="p-4">
            {/* Line number configuration */}
            <div className="mb-4 flex items-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
                Start Line Number:
              </label>
              <DebouncedInput
                type="number"
                min={1}
                value={startLineNumber}
                onChange={(value) => updateStartLineNumber(value as number)}
                className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              />
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                (Lines will be automatically numbered)
              </span>
            </div>

            {/* Passage Lines */}
            <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
              {passage.lines.map((line, lineIndex) => (
                <div
                  key={lineIndex}
                  className="flex border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                >
                  <div className="w-10 py-2 px-2 text-center text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600">
                    {line.number}
                  </div>
                  {editingLineIndex === lineIndex ? (
                    <div className="flex-1 flex">
                      <DebouncedInput
                        type="text"
                        value={line.text || ""}
                        onChange={(value) =>
                          updateLine(lineIndex, value as string)
                        }
                        className="flex-1 px-3 py-2 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/20 dark:bg-gray-800 dark:text-white"
                        autoFocus
                        onBlur={() => setEditingLineIndex(null)}
                      />
                    </div>
                  ) : (
                    <div
                      className="flex-1 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 flex justify-between items-center"
                      onClick={() => setEditingLineIndex(lineIndex)}
                    >
                      <span>{line.text}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          promptRemoveLine(lineIndex);
                        }}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 opacity-0 group-hover:opacity-100 hover:opacity-100"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {passage.lines.length === 0 && (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No lines added yet. Use the line editor to add content.
                </div>
              )}
            </div>

            {/* Passage Images */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">
                Passage Images
              </h3>
              <PassageImageManager
                images={passage.images || []}
                onImagesChange={(images) => {
                  onUpdate({
                    ...passage,
                    images,
                  });
                }}
                testId={testId}
              />
            </div>
          </div>
        )}
      </div>
      {/* Confirmation Modal for Line Deletion */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={removeLine}
        title="Delete Line"
        message={`Are you sure you want to delete line ${
          lineToDelete !== null && passage.lines[lineToDelete]
            ? passage.lines[lineToDelete].number
            : ""
        }? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Line Editor Modal */}
      {showLineEditorModal && (
        <LineEditorModal
          isOpen={showLineEditorModal}
          onClose={() => setShowLineEditorModal(false)}
          lines={passage.lines}
          startLineNumber={startLineNumber}
          onSave={(newLines) => {
            onUpdate({
              ...passage,
              lines: newLines,
            });
          }}
        />
      )}
    </>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(PassageEditor);
